import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Snackbar,
  Alert,
  InputAdornment,
  useTheme,
  useMediaQuery,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import MessageIcon from '@mui/icons-material/Message';
import SendIcon from '@mui/icons-material/Send';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import emailjs from '@emailjs/browser';

const Contact: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const formRef = useRef<HTMLFormElement>(null);
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [service, setService] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const serviceParam = params.get('service');
    const requestParam = params.get('request');
    
    if (serviceParam) {
      setService(serviceParam);
      
      let serviceMessage = '';
      
      switch(serviceParam) {
        case 'cmmi':
          serviceMessage = 'I am interested in learning more about your CMMI services.';
          break;
        case 'iso':
          serviceMessage = 'I would like information about your ISO certification services.';
          break;
        case 'digital':
          serviceMessage = 'I am interested in discussing digital transformation for my business.';
          break;
        case 'training':
          serviceMessage = 'I would like to inquire about your training programs.';
          break;
        default:
          serviceMessage = `I am interested in your ${serviceParam} services.`;
      }
      
      setMessage(serviceMessage);
    }
    
    if (requestParam === 'brochure') {
      setMessage('I would like to request your company brochure.');
    }
  }, []);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    emailjs.sendForm(
      'service_ce4lntl', // Your EmailJS service ID
      'template_quho45g', // Your EmailJS template ID
      formRef.current!,
      'BTs-4N5fsAcO3ag9X' // Your EmailJS public key
    )
      .then((result) => {
        console.log('Email successfully sent!', result.text);
        setSnackbarOpen(true);
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setService('');
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        setError('Failed to send your message. Please try again later.');
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const contactInfo = [
    {
      icon: <LocationOnIcon fontSize="large" />,
      title: 'Head Office',
      content: '1 Norana Avenue, Favona, Auckland, postal code-2024, New Zealand',
    },
    {
      icon: <LocationOnIcon fontSize="large" />,
      title: 'Regional Office',
      content: '43325 John Michael Ter, Ashburn, VA 20148, USA',
    },
    {
      icon: <EmailIcon fontSize="large" />,
      title: 'Email',
      content: 'info@rttlnz.com',
    },
    {
      icon: <PhoneIcon fontSize="large" />,
      title: 'Business Hours',
      content: '9:00 AM – 5:00 PM\nMonday – Saturday',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Contact Us
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Get in touch with our team
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          transform: 'translate(30%, 30%)',
          zIndex: 1
        }} />
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {/* Contact Form Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 6, 
            mt: -5,
            mb: 10,
            position: 'relative',
            zIndex: 10
          }}
        >
          <Box sx={{ flex: '1 1 500px' }}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: 5, 
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 50px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <Box sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  background: 'linear-gradient(90deg, #1976d2, #2196f3)'
                }} />
                
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  fontWeight="bold" 
                  color="#0c2340"
                  align={isMobile ? 'center' : 'left'}
                >
                  Send us a Message
                </Typography>
                <Typography 
                  paragraph 
                  color="text.secondary" 
                  sx={{ mb: 4 }}
                  align={isMobile ? 'center' : 'left'}
                >
                  Fill out the form below and we'll get back to you as soon as possible
                </Typography>
                
                <form ref={formRef} onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      margin="normal"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      margin="normal"
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      margin="normal"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      select
                      fullWidth
                      label="Service Interested In"
                      name="service"
                      margin="normal"
                      value={service}
                      onChange={(e) => setService(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessCenterIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    >
                      <MenuItem value="">Select a service (optional)</MenuItem>
                      <MenuItem value="cmmi">CMMI Services</MenuItem>
                      <MenuItem value="iso">ISO Services</MenuItem>
                      <MenuItem value="digital">Digital Services</MenuItem>
                      <MenuItem value="training">Training Services</MenuItem>
                      <MenuItem value="other">Other Services</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      label="Your Message"
                      name="message"
                      margin="normal"
                      required
                      multiline
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                            <MessageIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={loading}
                      endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{
                        mt: 2,
                        py: 1.5,
                        borderRadius: '10px',
                        background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                    
                    {error && (
                      <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {error}
                      </Typography>
                    )}
                  </Box>
                </form>
              </Paper>
            </motion.div>
          </Box>

          <Box sx={{ flex: '1 1 400px' }}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: 5, 
                  height: '100%',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                  color: 'white',
                  boxShadow: '0 10px 50px rgba(0,0,0,0.2)',
                }}
              >
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  fontWeight="bold"
                  sx={{ mb: 4 }}
                  align={isMobile ? 'center' : 'left'}
                >
                  Contact Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {contactInfo.map((info, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box 
                          sx={{ 
                            mr: 3, 
                            color: '#90caf9', 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 45,
                            height: 45,
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.1)',
                          }}
                        >
                          {info.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight="medium" gutterBottom>
                            {info.title}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8, whiteSpace: 'pre-line' }}>
                            {info.content}
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  ))}
                </Box>
                
                {/* Social Media Icons - Add if needed */}
                <Box sx={{ display: 'flex', gap: 3, mt: 5, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  {['LinkedIn', 'Twitter', 'Facebook'].map((social, index) => (
                    <motion.div
                      key={social}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                    >
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          background: 'rgba(255, 255, 255, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.2)',
                            transform: 'translateY(-5px)'
                          }
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {social[0]}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Box>
        </Box>
      </Container>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Thank you for contacting us! We'll get back to you shortly.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact; 